import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { apiClient } from 'shared/api';
import { validateINN } from 'shared/model/constants/validations';
import { ButtonCustom } from 'shared/ui/button-custom';
import { TextInputSmall } from 'shared/ui/text-input-small';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './agent-admin.module.scss';

export const AgentAdmin = () => {
  const { type, uid } = useParams();

  const [user, setUser] = useState<any>({});

  const [open, setOpen] = useState(false);
  const [openAcc, setOpenAcc] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [share, setShare] = useState('');
  const [shareAcc, setShareAcc] = useState('');
  const [shareErr, setShareErr] = useState(false);
  const [shareErrAcc, setShareErrAcc] = useState(false);

  // Состояние для редактирования
  const [editData, setEditData] = useState({
    bio: '',
    phone: '',
    email: '',
    inn: '',
  });
  const [editErrors, setEditErrors] = useState({
    bio: false,
    phone: false,
    email: false,
    inn: false,
  });

  const userTypeMap = () => {
    switch (user.user_type) {
      case 'agent':
        return 'Агент';
      case 'supplier':
        return 'Поставщик';
      case 'leasing':
        return 'Лизинговая компания';
      case 'client':
        return 'Клиент';
      default:
        return '-';
    }
  };

  const userSubtypeMap = () => {
    switch (user.user_subtype) {
      case 'legal':
        return 'Юридическое лицо';
      case 'entrepreneur':
        return 'Индивидуальный предприниматель';
      case 'individual':
        return 'Физическое лицо';
      case 'self-employed':
        return 'Самозанятый';
      default:
        return '-';
    }
  };

  const getPageTitle = (value?: string) => {
    switch (value) {
      case 'agents':
        return 'Список агентов TLC Group';
      case 'suppliers':
        return 'Список поставщиков TLC Group';
      case 'leasings':
        return 'Список лизинговых компаний TLC Group';
      case 'clients':
        return 'Список клиентов TLC Group';
      default:
        return 'Список пользователей TLC Group';
    }
  };

  const getAgentsArr = async () => {
    try {
      if (!type || !uid) throw Error('user type is required');

      const preparedType = type.slice(0, -1);

      const response = await apiClient.get(`/agents/find?user_type=${preparedType}&id=${uid}`);
      const data = Array.isArray(response.data.data) ? response.data.data[0] : {};

      setUser(data);
    } catch (e) {
      // Обработка ошибки получения данных агента
    }
  };

  useEffect(() => {
    getAgentsArr();
  }, [type, uid]);

  const setConfirm = async () => {
    await apiClient.post('/new-agent/confirm', {
      id: user.id,
      share: parseFloat(shareAcc),
    });
    getAgentsArr();
    handleCloseAcc();
  };

  const handleAgentAccept = async (user: any) => {
    if (user.user_type && user.user_type !== 'agent') {
      await setConfirm();
    } else if (user.user_type === 'agent') {
      setOpenAcc(true);
    }
  };

  const handleSetShareAcc = async () => {
    if (!user || !shareAcc) {
      setShareErrAcc(true);
      return;
    }
    try {
      if (!shareErrAcc) {
        await setConfirm();
      }
    } catch (e) {
      // Обработка ошибки установки доли
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedAgent(null);
    setShare('');
    setShareErr(false);
  };
  const handleCloseAcc = () => {
    setOpenAcc(false);
    setShareAcc('');
    setShareErrAcc(false);
  };

  const handleOpenEdit = () => {
    setEditData({
      bio: user.bio || '',
      phone: user.phone || '',
      email: user.email || '',
      inn: user.inn || '',
    });
    setEditErrors({
      bio: false,
      phone: false,
      inn: false,
      email: false,
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setEditData({
      bio: '',
      phone: '',
      email: '',
      inn: '',
    });
    setEditErrors({
      bio: false,
      phone: false,
      email: false,
      inn: false,
    });
  };

  const handleSetShare = async () => {
    if (!selectedAgent || !share) {
      setShareErr(true);
      return;
    }

    try {
      await apiClient.post('/new-agent/share', {
        email: selectedAgent.email,
        share: parseFloat(share),
      });
      getAgentsArr();
      handleClose();
    } catch (e) {
      // Обработка ошибки установки доли агента
    }
  };

  const handleChangeShareValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setShare(value);
    setShareErr(!value || Number(value) <= 0 || Number(value) > 1);
  };
  const handleChangeShareValueAcc = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setShareAcc(value);
    setShareErrAcc(!value || Number(value) <= 0 || Number(value) > 1);
  };

  const handleEditDataChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Валидация
    if (field === 'bio') {
      setEditErrors((prev) => ({ ...prev, bio: !value.trim() }));
    } else if (field === 'phone') {
      const phoneRegex = /^\+?[\d\s\-()]+$/;
      setEditErrors((prev) => ({ ...prev, phone: !value.trim() || !phoneRegex.test(value) }));
    } else if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEditErrors((prev) => ({ ...prev, email: !value.trim() || !emailRegex.test(value) }));
    } else if (field === 'inn') {
      setEditErrors((prev) => ({ ...prev, inn: !value.trim() || !validateINN(value) }));
    }
  };

  const handleSaveEdit = async () => {
    // Проверяем ошибки
    const hasErrors =
      editErrors.bio ||
      editErrors.phone ||
      editErrors.email ||
      editErrors.inn ||
      !editData.bio.trim() ||
      !editData.phone.trim() ||
      !editData.email.trim() ||
      !editData.inn.trim();

    if (hasErrors) {
      setEditErrors({
        bio: !editData.bio.trim(),
        phone: !editData.phone.trim() || !/^\+?[\d\s\-()]+$/.test(editData.phone),
        email: !editData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.email),
        inn: !editData.inn.trim() || !validateINN(editData.inn),
      });
      return;
    }

    try {
      await apiClient.put('/new-agent/update', {
        id: user.id,
        bio: editData.bio,
        phone: editData.phone,
        email: editData.email,
        inn: editData.inn
      });

      getAgentsArr();
      handleCloseEdit();
    } catch (e) {
      // Обработка ошибки обновления данных агента
    }
  };

  return (
    <Box className={styles.agentsPage}>
      <DashboardHeaderBackLink link={-1} title={getPageTitle()} />
      <Box>
        <Card className={styles.card}>
          <CardHeader title={user.bio} />
          <CardContent className={styles.cardContent}>
            <List>
              <ListItem className={styles.listItem}>
                <b>ИНН:</b>
                <span>{user.inn}</span>
              </ListItem>
              <ListItem className={styles.listItem}>
                <b>Почта: </b>
                <span>{user.email}</span>
              </ListItem>
              <ListItem className={styles.listItem}>
                <b>Телефон: </b>
                <span>{user.phone}</span>
              </ListItem>
              <ListItem className={styles.listItem}>
                <b>Тип пользователя: </b>
                <span>{userTypeMap()}</span>
              </ListItem>
              <ListItem className={styles.listItem}>
                <b>Тип клиента: </b>
                <span>{userSubtypeMap()}</span>
              </ListItem>
              <ListItem className={styles.listItem}>
                <b>Доля: </b>
                <span>{user.user_type === 'agent' && user.share ? user.share : '-'}</span>
              </ListItem>
              <ListItem className={styles.listItem}>
                <b>Подтвержден: </b>
                <span>{user.confirmed ? 'Да' : 'Нет'}</span>
              </ListItem>
              {user && !user.confirmed && (
                <ListItem className={styles.listItem}>
                  <ButtonCustom
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAgentAccept(user);
                    }}
                  >
                    Подтвердить агента
                  </ButtonCustom>
                </ListItem>
              )}
              {user && user.confirmed && user.user_type === 'agent' && (
                <ListItem className={styles.listItem}>
                  <ButtonCustom
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAgentAccept(user);
                    }}
                  >
                    Редактировать долю
                  </ButtonCustom>
                </ListItem>
              )}
              {user && (
                <ListItem className={styles.listItem}>
                  <ButtonCustom
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEdit();
                    }}
                  >
                    Редактировать данные
                  </ButtonCustom>
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '350px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='alert-dialog-title'>
          <span>Введите долю агента</span>
        </DialogTitle>
        <DialogContent className={styles.modalDescriptionWr}>
          <DialogContentText className={styles.modalDescription} id='alert-dialog-description'>
            <Box className={styles.offerDesc}>
              <span className={styles.appDescTitle}>Доля</span>
              <TextInputSmall
                onChange={handleChangeShareValue}
                value={share}
                error={shareErr}
                required
                name={'share'}
                type={'number'}
                placeholder={'Введите долю от 0 до 1'}
              />
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom className={styles.modalButtonCancel} onClick={handleClose}>
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.modalButton} onClick={handleSetShare}>
            Подтвердить
          </ButtonCustom>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAcc}
        onClose={handleCloseAcc}
        aria-labelledby='alert-dialog-titleAcc'
        aria-describedby='alert-dialog-descriptionAcc'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '350px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='alert-dialog-title'>
          <span>Введите долю агента</span>
        </DialogTitle>
        <DialogContent className={styles.modalDescriptionWr}>
          <DialogContentText className={styles.modalDescription} id='alert-dialog-descriptionAcc'>
            <Box className={styles.offerDesc}>
              <span className={styles.appDescTitle}>Доля</span>
              <TextInputSmall
                onChange={handleChangeShareValueAcc}
                value={shareAcc}
                error={shareErrAcc}
                required
                name={'shareAcc'}
                type={'number'}
                placeholder={'Введите долю от 0 до 1'}
              />
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom className={styles.modalButtonCancel} onClick={handleCloseAcc}>
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.modalButton} onClick={handleSetShareAcc}>
            Подтвердить
          </ButtonCustom>
        </DialogActions>
      </Dialog>

      {/* Модальное окно для редактирования данных */}
      <Dialog
        open={openEdit}
        onClose={handleCloseEdit}
        aria-labelledby='edit-dialog-title'
        aria-describedby='edit-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '500px',
            width: '100%',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='edit-dialog-title'>
          <span>Редактировать данные агента</span>
        </DialogTitle>
        <DialogContent className={styles.modalDescriptionWr}>
          <DialogContentText className={styles.modalDescription} id='edit-dialog-description'>
            {editData ? (
              <Box
                className={styles.offerDesc}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
              >
                <Box>
                  <span className={styles.appDescTitle}>Название/ФИО</span>
                  <TextInputSmall
                    onChange={handleEditDataChange('bio')}
                    value={editData.bio}
                    error={editErrors.bio}
                    required
                    name={'bio'}
                    type={'text'}
                    placeholder={'Введите название или ФИО'}
                  />
                </Box>

                <Box>
                  <span className={styles.appDescTitle}>Телефон</span>
                  <TextInputSmall
                    onChange={handleEditDataChange('phone')}
                    value={editData.phone}
                    error={editErrors.phone}
                    required
                    name={'phone'}
                    type={'tel'}
                    placeholder={'Введите номер телефона'}
                  />
                </Box>

                <Box>
                  <span className={styles.appDescTitle}>Email</span>
                  <TextInputSmall
                    onChange={handleEditDataChange('email')}
                    value={editData.email}
                    error={editErrors.email}
                    required
                    name={'email'}
                    type={'email'}
                    placeholder={'Введите email адрес'}
                  />
                </Box>

                <Box>
                  <span className={styles.appDescTitle}>ИНН</span>
                  <TextInputSmall
                    onChange={handleEditDataChange('inn')}
                    value={editData.inn}
                    error={editErrors.inn}
                    required
                    name={'inn'}
                    type={'text'}
                    placeholder={'Введите ИНН (10-12 цифр)'}
                  />
                </Box>
              </Box>
            ) : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom className={styles.modalButtonCancel} onClick={handleCloseEdit}>
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.modalButton} onClick={handleSaveEdit}>
            Сохранить
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
